import React, { useState, useContext, useEffect, useRef } from "react";
import {
  useTheme,
  FormControl,
  FormControlLabel,
  Radio,
  InputAdornment,
  Button,
  Divider,
} from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { Field, FormikErrors, FormikProps } from "formik";
import { RadioGroup, TextField } from "formik-mui";
import { DesktopDatePicker as DatePicker } from "formik-mui-lab";

import Maybe from "../../common/Maybe";
import { FormikAutocomplete } from "../../common/FormkikAutocomplete";
import { LoadingButton } from "@mui/lab";
import { ArrowForward, RequestQuote } from "@mui/icons-material";

import { countries } from "../../../data/countries";
import FamiliesRequest from "./Families";
import {
  HelpIconInterface,
  Microcopy,
  MicrocopyResource,
} from "../../../utilities/contentful";
import QuoteModalTabButton from "../../../layouts/Fixed/components/Topbar/QuoteModalTabButton";
import HelpIcon from "../../common/HelpIcon";
import FormHelpLabel from "../../common/FormHelpLabel";

import {
  updateCoverageAreaText,
  updateDateText,
  updatePeopleTab,
  updateTripProtectionText,
} from "./QuoteHelpers";

import AppContext from "../../../AppContext";
import {
  PrimaryInsured,
  PurchaseDetails,
} from "../../purchase/PurchaseData.interface";
import { config } from "@brand";

const micro = new Microcopy();

export const formatCurrency = (value: string) => {
  const removeNonNumeric = value
    .replace(/[^0-9\\.]+/g, "")
    .replace(".", "A")
    .replace(/\./g, "")
    .replace("A", ".");

  const toNumeric = Number(removeNonNumeric);

  return toNumeric === 0 ? "" : toNumeric.toFixed(2);
};

interface Props {
  microcopy: MicrocopyResource[];
  rootTabDetails: {
    coverageDuration: string;
    coverageArea: string;
    tripProtection: string;
    numPeople: string;
  };
  updateBoxText: Function;
  formikProps: FormikProps<PurchaseDetails>;
  setDatesTabText: Function;
  setProtectionTabText: Function;
  setAreaTabText: Function;
  setPeopleTabText: Function;
  startDateTabText: { startDateText: string; datesValid: boolean };
  endDateTabText: { endDateText: string; datesValid: boolean };
  areaTabText: { coverageAreaText: string; coverageValid: boolean };
  protectionTabText: { tripProtectionText: string; protectionValid: boolean };
  peopleTabText: { peopleText: string; peopleValid: boolean };
  helpIcons: HelpIconInterface[];
}

const QuoteForm = (props: Props): JSX.Element => {
  const [debug, setDebug] = useState(false);
  const appState: any = useContext(AppContext);

  const handleOpenImportModal = () => {
    appState.setAppState({
      ...appState.state,
      quoteModalOpen: false,
      importInfoModalOpen: true,
    });
  };

  const theme = useTheme();

  const toggleDebug = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDebug(event.target.checked);
  };

  const changeTab = (newTab: number) => {
    appState.setAppState({
      ...appState.state,
      selectedTab: newTab,
    });
  };

  const resetTabTextData = async () => {
    await props.setDatesTabText({
      startDateText: "Start Date",
      endDateText: "End Date",
      datesValid: false,
    });

    await props.setAreaTabText({
      coverageAreaText: "Where do you need coverage?",
      coverageValid: false,
    });

    await props.setProtectionTabText({
      tripProtectionText: "Would you like additional coverage?",
      protectionValid: false,
    });

    await props.setPeopleTabText({
      peopleText: "Who needs coverage?",
      peopleValid: false,
    });
  };

  const handleSetDatesTabText = () => {
    props.setDatesTabText({
      startDateText: updateDateText(
        props.formikProps.values.startDate,
        props.formikProps.errors.startDate,
        "Start",
      ),
      endDateText: updateDateText(
        props.formikProps.values.endDate,
        props.formikProps.errors.endDate,
        "End",
      ),
      datesValid:
        !props.formikProps.errors.startDate &&
        !props.formikProps.errors.endDate,
    });
  };

  const handleSetAreaTabText = () => {
    props.setAreaTabText({
      coverageAreaText: updateCoverageAreaText(
        props.formikProps.values.usaTrip,
        props.formikProps.values.countryObject,
      ),
      coverageValid:
        !props.formikProps.errors.usaTrip &&
        !props.formikProps.errors.countryObject,
    });
  };

  const handleSetProtectionTabText = () => {
    props.setProtectionTabText({
      tripProtectionText: updateTripProtectionText(
        props.formikProps.values.tripProtection,
      ),
      protectionValid:
        !props.formikProps.errors.tripProtection &&
        !props.formikProps.errors.useOverallTripCost &&
        !props.formikProps.errors.initialPaymentDate,
    });
  };

  const handleSetPeopleTabText = () => {
    props.setPeopleTabText({
      peopleText: updatePeopleTab(
        props.formikProps.values.primaryInsured,
        props.formikProps.errors.primaryInsured as FormikErrors<
          Partial<PrimaryInsured>
        >[],
      ),
      peopleValid: props.formikProps.errors.primaryInsured === undefined,
    });
  };

  const handleSetAllTabText = () => {
    handleSetDatesTabText();
    handleSetAreaTabText();
    handleSetProtectionTabText();
    handleSetPeopleTabText();
  };

  const handleTabChange = (
    event: React.SyntheticEvent | null,
    toTab: number,
    fromTab?: number,
    allowChange?: boolean,
  ) => {
    let denyTabChange = false;

    if (fromTab === 0) {
      // validate start date, end date
      props.formikProps.setFieldTouched("startDate");
      props.formikProps.setFieldTouched("endDate");

      handleSetDatesTabText();

      denyTabChange = !!(
        props.formikProps.errors.startDate || props.formikProps.errors.endDate
      );
    } else if (fromTab === 1) {
      // validate coverage area, primary coverage country
      props.formikProps.setFieldTouched("usaTrip");

      if (!("countryObject" in props.formikProps.values)) {
        props.formikProps.setFieldValue("countryObject", {
          label: "Select a Country",
          value: "none",
        });
      }

      props.formikProps.setFieldTouched("countryObject");

      handleSetAreaTabText();

      denyTabChange = !!(
        props.formikProps.errors.usaTrip ||
        props.formikProps.errors.countryObject
      );
    } else if (fromTab === 2) {
      // validate trip protection, overall, initial payment date, overall cost
      props.formikProps.setFieldTouched("tripProtection");
      props.formikProps.setFieldTouched("useOverallTripCost");
      props.formikProps.setFieldTouched("initialPaymentDate");
      props.formikProps.setFieldTouched("overallTripCost");

      handleSetProtectionTabText();

      denyTabChange = !!(
        props.formikProps.errors.tripProtection ||
        props.formikProps.errors.useOverallTripCost ||
        props.formikProps.errors.initialPaymentDate
      );
    } else if (fromTab === 3) {
      handleSetPeopleTabText();
    }

    if (!denyTabChange || allowChange) changeTab(toTab);
  };

  const handleReset = () => {
    props.formikProps.resetForm();
    props.formikProps.setTouched({}, false);
    handleTabChange(null, 0, undefined);
    resetTabTextData();
  };

  const STEP_COUNT = 4;
  const stepStrip = useRef<HTMLDivElement>(null);

  // On a phone the steps scroll sideways, so the one being answered has to
  // bring itself into view; otherwise pressing Next appears to do nothing.
  useEffect(() => {
    const strip = stepStrip.current;
    const step = strip?.children[appState.state.selectedTab] as
      | HTMLElement
      | undefined;

    if (!strip || !step || strip.scrollWidth <= strip.clientWidth) return;

    strip.scrollTo({ left: step.offsetLeft - 16, behavior: "smooth" });
  }, [appState.state.selectedTab]);

  return (
    <Box
      component={"form"}
      noValidate={true}
      sx={{
        overflowY: "auto",
        "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
          padding: 0,
        },
        "& .MuiOutlinedInput-input": {
          background: theme.palette.background.paper,
          padding: 2,
        },
      }}
    >
      <Grid container>
        <Grid
          item
          md={3}
          sx={{
            width: "100%",
            position: "relative",
            backgroundColor: "#2c4d7a",
          }}
        >
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#2c4d7a",
              color: "#c7d6ea",
              fontSize: "12px",
              padding: "8px 14px 0",
            }}
          >
            <span>
              Step {appState.state.selectedTab + 1} of {STEP_COUNT}
            </span>
            <span>Swipe for the other steps &rsaquo;</span>
          </Box>

          <Box
            ref={stepStrip}
            sx={{
              backgroundColor: "#2c4d7a",
              // Stacked, the five steps fill a phone screen and push the
              // question itself below the fold, so they scroll sideways. The
              // part-visible next step and the fade say so without a label.
              display: { xs: "flex", md: "block" },
              overflowX: { xs: "auto", md: "visible" },
              scrollSnapType: { xs: "x proximity", md: "none" },
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              "& > *": {
                flex: { xs: "0 0 58%", sm: "0 0 34%", md: "unset" },
                scrollSnapAlign: { xs: "start", md: "none" },
              },
              "& .MuiButtonBase-root": {
                padding: { xs: "12px 14px", md: "20px" },
                minHeight: { xs: "68px", md: "75px" },
              },
            }}
          >
          <Box
            sx={{ "& :hover": { backgroundColor: "#425f88" } }}
            onClick={(e) =>
              handleTabChange(e, 0, appState.state.selectedTab, true)
            }
          >
            <QuoteModalTabButton
              selected={appState.state.selectedTab === 0}
              title={micro.getFromKey(
                "heading.coverage-duration",
                props.microcopy,
              )}
              fieldData={props.startDateTabText.startDateText}
              completed={props.startDateTabText.datesValid}
              submitted={props.formikProps.submitCount > 0}
              contentfulHelpId="3uSjqlzZ7FVbNSbuHGpQRH"
              helpIcons={props.helpIcons}
            />
          </Box>
          <Box
            sx={{ "& :hover": { backgroundColor: "#425f88" } }}
            onClick={(e) =>
              handleTabChange(e, 1, appState.state.selectedTab, true)
            }
          >
            <QuoteModalTabButton
              selected={appState.state.selectedTab === 1}
              title={micro.getFromKey("heading.coverage-area", props.microcopy)}
              fieldData={props.areaTabText.coverageAreaText}
              completed={props.areaTabText.coverageValid}
              submitted={props.formikProps.submitCount > 0}
              contentfulHelpId="DK74bRdhnk1DyQtyk4wBj"
              helpIcons={props.helpIcons}
            />
          </Box>
          <Box
            sx={{ "& :hover": { backgroundColor: "#425f88" } }}
            onClick={(e) =>
              handleTabChange(e, 2, appState.state.selectedTab, true)
            }
          >
            <QuoteModalTabButton
              selected={appState.state.selectedTab === 2}
              title={micro.getFromKey(
                "heading.trip-protection",
                props.microcopy,
              )}
              fieldData={props.protectionTabText.tripProtectionText}
              completed={props.protectionTabText.protectionValid}
              submitted={props.formikProps.submitCount > 0}
              contentfulHelpId="70BSG6th11qPvIpX1Tr9kQ"
              helpIcons={props.helpIcons}
            />
          </Box>
          <Box
            sx={{ "& :hover": { backgroundColor: "#425f88" } }}
            onClick={(e) =>
              handleTabChange(e, 3, appState.state.selectedTab, true)
            }
          >
            <QuoteModalTabButton
              selected={appState.state.selectedTab === 3}
              title={micro.getFromKey("heading.number-people", props.microcopy)}
              fieldData={props.peopleTabText.peopleText}
              completed={props.peopleTabText.peopleValid}
              submitted={props.formikProps.submitCount > 0}
              contentfulHelpId="3qF7IdUMfNPZWhICDnsg5L"
              helpIcons={props.helpIcons}
            />
          </Box>
          </Box>

          <Box
            aria-hidden
            sx={{
              display: { xs: "block", md: "none" },
              position: "absolute",
              right: 0,
              bottom: 0,
              width: "36px",
              height: "68px",
              pointerEvents: "none",
              background:
                "linear-gradient(to right, rgba(44, 77, 122, 0), #2c4d7a)",
            }}
          />
        </Grid>

        <Grid item md={9}>
          <Box
            sx={{
              padding: { xs: "16px", sm: "30px" },
              display: appState.state.selectedTab === 0 ? "" : "none",
            }}
          >
            <Grid container rowSpacing={2} columnSpacing={4}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight={400}
                  fontSize="20px"
                  gutterBottom
                >
                  {micro.getFromKey("quote.coverage-dates", props.microcopy)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormHelpLabel
                    helpIcons={props.helpIcons}
                    label={micro.getFromKey(
                      "field.departure-date",
                      props.microcopy,
                    )}
                    contentfulId={"7qneUGjcohPse3Eg9Oa9mn"}
                  />

                  <Field
                    component={DatePicker}
                    name="startDate"
                    minDate={new Date(new Date().setHours(24, 0, 0, 0))}
                    textField={{
                      helperText: micro.getFromKey(
                        "field.departure-date-placeholder",
                        props.microcopy,
                      ),
                    }}
                    inputFormat="MM/dd/yyyy"
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormHelpLabel
                    helpIcons={props.helpIcons}
                    label={micro.getFromKey(
                      "field.return-date",
                      props.microcopy,
                    )}
                    contentfulId={"2BtqRWrOW6qUyhflm5WkY1"}
                  />
                  <Field
                    component={DatePicker}
                    name="endDate"
                    minDate={new Date(new Date().setHours(48, 0, 0, 0))}
                    textField={{
                      helperText: micro.getFromKey(
                        "field.return-date-placeholder",
                        props.microcopy,
                      ),
                    }}
                    inputFormat="MM/dd/yyyy"
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              mt={3}
            >
              <Maybe test={!appState.state.quoteData.postDeparture}>
                <Button
                  onClick={(e) => handleTabChange(e, 1, 0)}
                  color={"success"}
                  variant={"contained"}
                  endIcon={<ArrowForward />}
                >
                  Next
                </Button>
              </Maybe>

              <Maybe test={appState.state.quoteData.postDeparture}>
                <LoadingButton
                  onClick={() => {
                    handleSetAllTabText();
                    props.formikProps.submitForm();
                  }}
                  endIcon={<RequestQuote />}
                  loading={props.formikProps.isSubmitting}
                  loadingPosition="end"
                  variant="contained"
                >
                  {micro.getFromKey("button.get-quote", props.microcopy)}
                </LoadingButton>
              </Maybe>
            </Box>
          </Box>

          <Box
            sx={{
              padding: { xs: "16px", sm: "30px" },
              display: appState.state.selectedTab === 1 ? "" : "none",
            }}
          >
            <Grid container rowSpacing={2} columnSpacing={4}>
              <Grid item xs={12}>
                <FormHelpLabel
                  helpIcons={props.helpIcons}
                  label={micro.getFromKey(
                    "field.coverage-usa",
                    props.microcopy,
                  )}
                  contentfulId={"4q7hhcihv4yZxvVqwm9St6"}
                />
                <Field component={RadioGroup} name="usaTrip" row>
                  <FormControlLabel
                    value="yes"
                    disabled={props.formikProps.isSubmitting}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    disabled={props.formikProps.isSubmitting}
                    control={<Radio />}
                    label="No"
                  />
                </Field>
              </Grid>

              <Maybe test={props.formikProps.values?.usaTrip === "no"}>
                <Grid item xs={12}>
                  <FormHelpLabel
                    helpIcons={props.helpIcons}
                    label={micro.getFromKey(
                      "field.primary-country",
                      props.microcopy,
                    )}
                    contentfulId={"5OJFTKfipjEB0MmWCqJshC"}
                  />
                  <Field
                    component={FormikAutocomplete}
                    name="countryObject"
                    options={[
                      { label: "Select a Country", value: "none" },
                      ...countries,
                    ]}
                  />
                </Grid>
              </Maybe>
            </Grid>

            <Grid container mt={3}>
              <Grid item xs={12}>
                <HelpIcon
                  helpIcons={props.helpIcons}
                  contentfulId={"5EwBLiuzlO8Iybzxr2pdDG"}
                  color={"dark"}
                  iconText={micro.getFromKey(
                    "field.coverage-multiple-countries",
                    props.microcopy,
                  )}
                />
              </Grid>
            </Grid>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              mt={3}
            >
              <Maybe test={!appState.state.quoteData.postDeparture}>
                <Button
                  onClick={(e) => handleTabChange(e, 2, 1)}
                  color={"success"}
                  variant={"contained"}
                  endIcon={<ArrowForward />}
                >
                  Next
                </Button>
              </Maybe>

              <Maybe test={appState.state.quoteData.postDeparture}>
                <LoadingButton
                  onClick={() => {
                    handleSetAllTabText();
                    props.formikProps.submitForm();
                  }}
                  endIcon={<RequestQuote />}
                  loading={props.formikProps.isSubmitting}
                  loadingPosition="end"
                  variant="contained"
                >
                  {micro.getFromKey("button.get-quote", props.microcopy)}
                </LoadingButton>
              </Maybe>
            </Box>
          </Box>

          <Box
            sx={{
              padding: { xs: "16px", sm: "30px" },
              display: appState.state.selectedTab === 2 ? "" : "none",
            }}
          >
            <Grid container rowSpacing={2} columnSpacing={4}>
              <Maybe test={config.allowSelectTripProtection}>
                <Grid item xs={12}>
                  <FormHelpLabel
                    helpIcons={props.helpIcons}
                    label={micro.getFromKey(
                      "field.trip-cancellation",
                      props.microcopy,
                    )}
                    contentfulId={"2SHUaxYocBaI4xy2CtQbQ8"}
                  />
                  <Field
                    component={RadioGroup}
                    name="tripProtection"
                    props={{ name: "tripProtection" }}
                    row
                  >
                    <FormControlLabel
                      value="yes"
                      disabled={props.formikProps.isSubmitting}
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      disabled={props.formikProps.isSubmitting}
                      control={<Radio />}
                      label="No"
                    />
                  </Field>
                </Grid>
              </Maybe>

              <Maybe test={props.formikProps.values?.tripProtection == "yes"}>
                <Grid item xs={12}>
                  <Maybe test={config.allowDefineIndivTripCost}>
                    <Field component={RadioGroup} name="useOverallTripCost">
                      <FormHelpLabel
                        helpIcons={props.helpIcons}
                        label={micro.getFromKey(
                          "field.cost-input-choice",
                          props.microcopy,
                        )}
                        contentfulId={"5d3ziCNh9lcX7uOhlJuCip"}
                      />
                      <FormControlLabel
                        value="yes"
                        disabled={props.formikProps.isSubmitting}
                        control={<Radio />}
                        label={micro.getFromKey(
                          "option.one-trip-cost",
                          props.microcopy,
                        )}
                      />
                      <FormControlLabel
                        value="no"
                        disabled={props.formikProps.isSubmitting}
                        control={<Radio />}
                        label={micro.getFromKey(
                          "option.individual-trip-cost",
                          props.microcopy,
                        )}
                      />
                    </Field>
                  </Maybe>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <FormHelpLabel
                      helpIcons={props.helpIcons}
                      label={micro.getFromKey(
                        "field.initial-payment-date",
                        props.microcopy,
                      )}
                      contentfulId={"4MRZ6JdJZVErsxSAkWMU1m"}
                    />
                    <Field
                      component={DatePicker}
                      name="initialPaymentDate"
                      inputFormat="MM/dd/yyyy"
                      maxDate={new Date(new Date().setHours(0, 0, 0, 0))}
                    />
                  </FormControl>
                </Grid>
              </Maybe>
            </Grid>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              mt={3}
            >
              <Maybe test={!appState.state.quoteData.postDeparture}>
                <Button
                  onClick={(e) => handleTabChange(e, 3, 2)}
                  color={"success"}
                  variant={"contained"}
                  endIcon={<ArrowForward />}
                >
                  Next
                </Button>
              </Maybe>

              <Maybe test={appState.state.quoteData.postDeparture}>
                <LoadingButton
                  onClick={() => {
                    handleSetAllTabText();
                    props.formikProps.submitForm();
                  }}
                  endIcon={<RequestQuote />}
                  loading={props.formikProps.isSubmitting}
                  loadingPosition="end"
                  variant="contained"
                >
                  {micro.getFromKey("button.get-quote", props.microcopy)}
                </LoadingButton>
              </Maybe>
            </Box>
          </Box>

          <Box
            sx={{
              padding: { xs: "16px", sm: "30px" },
              display: appState.state.selectedTab === 3 ? "" : "none",
            }}
          >
            <Maybe test={config.allowSpreadSheetImportTool}>
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <Button
                  onClick={handleOpenImportModal}
                  variant={"contained"}
                  color={"secondary"}
                >
                  Open Spreadsheet Import Tool
                </Button>
              </Box>
            </Maybe>

            <Maybe
              test={
                props.formikProps.values?.tripProtection == "yes" &&
                props.formikProps.values?.useOverallTripCost == "yes"
              }
            >
              <Divider sx={{ my: 2 }} />

              <Grid container mb={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <FormHelpLabel
                      helpIcons={props.helpIcons}
                      label={micro.getFromKey(
                        "field.overall-cost",
                        props.microcopy,
                      )}
                      contentfulId={"33HX9Df7WMJIEZRSjERCiE"}
                    />
                    <Field
                      component={TextField}
                      placeholder={"0.00"}
                      name="overallTripCost"
                      type="text"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      onChange={props.formikProps.handleChange}
                      onBlur={(event: any) => {
                        const formatted = formatCurrency(
                          props.formikProps.values?.overallTripCost as string,
                        );
                        props.formikProps.setFieldValue(
                          "overallTripCost",
                          formatted,
                        );
                        props.formikProps.handleBlur(event);
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Maybe>

            <Divider sx={{ my: 2 }} />

            <FamiliesRequest
              helpIcons={props.helpIcons}
              primaryInsured={props.formikProps.values?.primaryInsured}
              microcopy={props.microcopy}
            />

            <Grid container mt={4}>
              <Grid item sm={12}>
                <Box display="flex" alignItems="center" flexDirection="column">
                  <LoadingButton
                    onClick={() => {
                      handleSetAllTabText();
                      props.formikProps.submitForm();
                    }}
                    endIcon={<RequestQuote />}
                    loading={props.formikProps.isSubmitting}
                    loadingPosition="end"
                    variant="contained"
                  >
                    {micro.getFromKey("button.get-quote", props.microcopy)}
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
            <Maybe
              test={
                props.formikProps.submitCount > 0 && !props.formikProps.isValid
              }
            >
              <Box textAlign={"center"} mt={3}>
                <Typography
                  color={"error"}
                  variant={"body1"}
                  fontWeight={600}
                  fontStyle={"italic"}
                >
                  You have errors or incomplete items in your quote request.
                </Typography>
              </Box>
            </Maybe>
          </Box>
        </Grid>
      </Grid>
      {/* 
      <br /><br />
      <Paper elevation={3} sx={{padding: 4}}>

        <Grid item container justifyContent="center" xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={debug}
                  onChange={toggleDebug}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              }
              label="Toggle Debug Info"
            />
        </Grid>

        <Maybe test={debug}>
          <Grid item container justifyContent="center" xs={12}>
            <Typography
              variant={'body1'}
              gutterBottom
              align={'center'}
            >
              {props.formikProps.isSubmitting ? 'submitting' : 'not submitting'}
              <br></br>
              {props.formikProps.isValidating ? 'validating' : 'not validating'}
              <br></br>
              {props.formikProps.isValid ? 'valid' : 'not valid'}
            </Typography>
          </Grid>

          <pre style={{ textAlign: "left" }}>
            <strong>Values</strong>
            <br />
            {JSON.stringify(props.formikProps.values, null, 2)}
          </pre>
          <pre style={{ textAlign: "left" }}>
            <strong>Errors</strong>
            <br />
            {JSON.stringify(props.formikProps.errors, null, 2)}
          </pre>
          <pre style={{ textAlign: "left" }}>
            <strong>Touched</strong>
            <br />
            {JSON.stringify(props.formikProps.touched, null, 2)}
          </pre>
        </Maybe>
      </Paper> */}
    </Box>
  );
};

export default QuoteForm;
